import { Box, Flex, Tag, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import type { FieldStatusDto } from "../../../../../../autogen/bff-api";
import { FieldErrorBox } from "./FieldErrorBox";
import { ReadinessBadge } from "./ReadinessBadge";
import { FieldSourceMark } from "./FieldSourceMark";

export type SummaryFieldSpan = "full" | "half";

export interface SummaryFieldProps {
  label: string;
  value: ReactNode;
  span?: SummaryFieldSpan;
  status?: FieldStatusDto;
  // Additional readiness statuses whose actionable errors render below the
  // primary value (e.g. MainCpvCodeValid alongside MainCpvCodeSet). The badge
  // still tracks `status`; this is purely for inline error boxes.
  extraStatuses?: ReadonlyArray<FieldStatusDto | undefined>;
  changed?: boolean;
  sourcePaths?: ReadonlyArray<string>;
  testId?: string;
}

// SummaryFieldGroup reads `span` off the React element's props to bin-pack halves.
// Mark it on the component so the parent can detect SummaryField children.
SummaryField.displayName = "SummaryField";

interface WithError extends FieldStatusDto {
  error: NonNullable<FieldStatusDto["error"]>;
}

// The generic "Required" code is already communicated by the red badge next to
// the label plus the empty value placeholder ("Not provided"). Rendering an
// additional red box for it just repeats the same message three times. The box
// is reserved for codes that add information the badge can't carry — parameterized
// errors (UnknownCpvCode, InvalidLanguageCode, ...) and conditional rules.
const REDUNDANT_WITH_BADGE = new Set(["zz_FieldErrorCode_Required"]);

export function SummaryField({ label, value, status, extraStatuses, changed, sourcePaths, testId }: SummaryFieldProps) {
  const { t } = useTranslation();
  const errorStatuses = [status, ...(extraStatuses ?? [])]
    .filter((s): s is WithError => !!s?.error)
    .filter((s) => !REDUNDANT_WITH_BADGE.has(s.error.code));
  return (
    <Box py="2" data-testid={testId}>
      <SummaryFieldLabel>
        <ReadinessBadge status={status} />
        {label}
        {changed && (
          <Tag size="sm" colorScheme="teal" variant="subtle" ml="2" textTransform="none" letterSpacing="normal">
            {t("Changed")}
          </Tag>
        )}
        <FieldSourceMark paths={sourcePaths} fieldLabel={label} />
      </SummaryFieldLabel>
      <Box fontSize="sm" fontWeight="medium" lineHeight="1.5">
        {value}
      </Box>
      {errorStatuses.map((s, i) => (
        <FieldErrorBox key={`${s.fieldKey}-${i}`} error={s.error} />
      ))}
    </Box>
  );
}

export const SummaryFieldLabel = ({ children }: { children: ReactNode }) => (
  <Flex as={Text} align="center" fontSize="xs" textTransform="uppercase" letterSpacing="wider" color="smMuted" mb="1">
    {children}
  </Flex>
);

interface EmptyValueProps {
  text: string;
}

export const EmptyValue = ({ text }: EmptyValueProps) => (
  <Text as="em" color="smMuted" fontStyle="italic" fontWeight="normal" fontSize="inherit">
    {text}
  </Text>
);
